import React, { useState, useMemo } from 'react';
import { useProfile } from '../../../context/ProfileContext';
import { useNavigate } from 'react-router-dom';
import UserProfilePicture from '../../../components/profile/UserProfilePicture';
import toast from 'react-hot-toast';

const AccountsTab = ({ onOpenDeleteArtist }) => {
  const { 
    userProfile, 
    activeProfile, 
    linkedArtistAccounts = [], 
    switchProfile 
  } = useProfile();

  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  // Filter out soft-deleted accounts
  const activeArtistAccounts = useMemo(() => {
    return linkedArtistAccounts.filter((artist) => artist.isDeleted !== true);
  }, [linkedArtistAccounts]);

  // Filter based on search query and sort alphabetically
  const filteredArtistAccounts = useMemo(() => {
    return activeArtistAccounts
      .filter((artist) =>
        (artist.artistName || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
      .sort((a, b) => (a.artistName || '').localeCompare(b.artistName || ''));
  }, [activeArtistAccounts, searchQuery]);

  const handleSwitchAccount = (account) => {
    if (switchProfile) {
      switchProfile(account);
      const targetTab = account.type === 'artist' ? 'discography' : 'library';
      toast.success(
        `Switched to ${account.type === 'artist' ? (account.artistName || "Artist") : (account.displayName || "Listener")}`
      );
      navigate(`/profile?tab=${targetTab}`, { replace: true });
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      
      {/* 1. PRIMARY LISTENER ACCOUNT CARD */}
      <section className="p-5 bg-white dark:bg-slate-900/80 border border-slate-100 dark:border-slate-800 rounded-2xl shadow-sm space-y-3 transition-colors duration-300">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-300">
          Primary Account (Listener)
        </span>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <UserProfilePicture 
              photoURL={userProfile?.photoURL} 
              sizeClass="w-12 h-12" 
              isPremium={userProfile?.premiumSubscription?.status === 'active'}
            />
            <div>
              <h3 className="font-bold text-slate-900 dark:text-white transition-colors">
                {userProfile?.displayName || userProfile?.name || "Listener Profile"}
              </h3>
              {/* FIXED: High contrast email text */}
              <p className="text-xs text-slate-500 dark:text-slate-400">{userProfile?.email}</p>
            </div>
          </div>

          {activeProfile?.type === 'user' ? (
            /* FIXED: High contrast emerald badge */
            <span className="px-3 py-1 text-xs font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-950/80 dark:text-emerald-300 border dark:border-emerald-800/50 rounded-full">
              Active
            </span>
          ) : (
            <button
              onClick={() => handleSwitchAccount({ ...userProfile, type: 'user' })}
              className="px-4 py-2 text-xs font-bold text-brand-pink border border-brand-pink/40 rounded-xl hover:bg-brand-pink/10 transition"
            >
              Switch
            </button>
          )}
        </div>
      </section>

      {/* 2. MANAGED ARTIST ACCOUNTS SECTION */}
      <section className="p-5 bg-white dark:bg-slate-900/80 border border-slate-100 dark:border-slate-800 rounded-2xl shadow-sm space-y-4 transition-colors duration-300">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-slate-900 dark:text-white transition-colors">Artist Accounts</h3>
            {/* FIXED: High contrast description text */}
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Switch or manage your {activeArtistAccounts.length} active artist profiles
            </p>
          </div>
          <span className="text-xs font-bold px-2.5 py-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-full transition-colors">
            {activeArtistAccounts.length}
          </span>
        </div>

        {/* Create New Artist Trigger */}
        <button
          onClick={() => navigate('/artist-account-form')}
          className="w-full py-3 border-2 border-dashed border-brand-pink/50 text-brand-pink hover:bg-brand-pink/10 rounded-xl text-xs font-bold uppercase tracking-wider transition-all active:scale-[0.98]"
        >
          + Create New Artist Account
        </button>

        {/* FIXED: High contrast search input & placeholder */}
        {activeArtistAccounts.length > 5 && (
          <input
            type="text"
            placeholder="Search your artist accounts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2.5 text-sm bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-400 focus:outline-none focus:border-brand-pink transition-colors"
          />
        )}

        {/* Scrollable Artist Accounts List */}
        <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-[50vh] overflow-y-auto pr-1 custom-scrollbar">
          {filteredArtistAccounts.length > 0 ? (
            filteredArtistAccounts.map((artist) => {
              const isActive = activeProfile?.id === artist.id && activeProfile?.type === 'artist';
              return (
                <div key={artist.id} className="py-3 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <UserProfilePicture photoURL={artist.photoURL} sizeClass="w-10 h-10" />
                    <div>
                      <p className="text-sm font-semibold text-slate-900 dark:text-white transition-colors">
                        {artist.artistName || "Artist Profile"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {isActive ? (
                      <span className="px-3 py-1 text-xs font-bold bg-brand-pink/20 text-brand-pink border border-brand-pink/30 rounded-full">
                        Active
                      </span>
                    ) : (
                      <button
                        onClick={() => handleSwitchAccount(artist)}
                        className="px-3 py-1.5 text-xs font-bold bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition"
                      >
                        Switch
                      </button>
                    )}

                    <button
                      onClick={() => onOpenDeleteArtist(artist)}
                      className="p-1.5 text-slate-400 dark:text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition"
                      title="Delete Artist Profile"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="py-4 text-xs text-center text-slate-500 dark:text-slate-400 italic">
              {searchQuery ? "No matching artist accounts found." : "No active artist accounts created yet."}
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

export default AccountsTab;